import { format } from 'date-fns';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useHoverOpen } from '@/hooks/use-hover-open';
import { ExceptionLog } from '../types/exception-log';
import {
  formatExceptionValue,
  formatExceptionHeading,
  DATE_TIME_FORMAT,
} from '../utils/format-exception-value';

interface ExceptionTooltipContentProps {
  exceptionLogs: ExceptionLog[];
  trigger: React.ReactNode;
  triggerLabel?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function ExceptionTooltipContent({
  exceptionLogs,
  trigger,
  triggerLabel = 'View exception details',
  side = 'left',
}: ExceptionTooltipContentProps) {
  // Controlled open (useHoverOpen): stays open while the cursor is over the
  // trigger or the popover box, closes shortly after leaving both; click
  // toggles.
  const { open, triggerProps, contentProps } = useHoverOpen();

  if (exceptionLogs.length === 0) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={open}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={triggerLabel}
            className="inline-flex cursor-pointer"
            {...triggerProps}
          >
            {trigger}
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-sm" {...contentProps}>
          <div className="space-y-3 py-2">
            {exceptionLogs.map((log, index) => (
              <div key={log.id}>
                {index > 0 && <div className="border-t my-2 border-white/20" />}
                <p className="font-medium text-xs mb-1">
                  {formatExceptionHeading(log)}
                </p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>
                    Old: {formatExceptionValue(log.old_value, log.exception_type, true)} →{' '}
                    New: {formatExceptionValue(log.new_value, log.exception_type, false)}
                  </div>
                  {log.reason && (
                    <div className="text-xs opacity-75 italic">{log.reason}</div>
                  )}
                  <div className="text-xs opacity-75">
                    {format(new Date(log.created_at), DATE_TIME_FORMAT)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
